/**
 * FloorPlanLegend Component
 * Draggable legend panel that displays all unique symbols used on a floor plan
 * Positioned inside the canvas and transforms with pan/zoom for print-ready output
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GripVertical } from 'lucide-react';
import type { LightingFixture, ControlType, LegendConfig } from '@/types/floorPlanViewer';
import type { LVProduct } from '@/features/product-library/types';
import { useLegendData } from './useLegendData';
import { LegendCategory } from './LegendCategory';

interface FloorPlanLegendProps {
  fixtures: LightingFixture[];
  controls: ControlType[];
  lvProducts: LVProduct[];
  config: LegendConfig | undefined;
  scale: number;
  isPublicView?: boolean;
  isSimRunning?: boolean;
  onConfigChange: (config: Partial<LegendConfig>) => void;
}

// Default position (bottom-right area of typical floor plan)
const DEFAULT_POSITION = { x: 50, y: 50 };

// Inline styles for reliable html2canvas capture
const styles = {
  container: {
    position: 'absolute' as const,
    backgroundColor: '#ffffff',
    border: '1px solid #000000',
    padding: '12px 16px',
    minWidth: '180px',
    maxWidth: '280px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    userSelect: 'none' as const,
    zIndex: 50,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderBottom: '2px solid #000000',
    paddingBottom: '8px',
    marginBottom: '12px',
    cursor: 'grab',
  },
  headerDragging: {
    cursor: 'grabbing',
  },
  title: {
    fontSize: '12px',
    fontWeight: 700,
    textAlign: 'center' as const,
    letterSpacing: '2px',
    textTransform: 'uppercase' as const,
    color: '#000000',
  },
  dragIcon: {
    color: '#9CA3AF',
    flexShrink: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  loading: {
    fontSize: '10px',
    color: '#6B7280',
    textAlign: 'center' as const,
    padding: '8px',
  },
  empty: {
    fontSize: '10px',
    color: '#9CA3AF',
    textAlign: 'center' as const,
    padding: '8px',
    fontStyle: 'italic' as const,
  },
};

// Constants for resize bounds
const MIN_WIDTH = 160;
const MAX_WIDTH = 1000; // Increased to allow larger legends for complex floor plans
const DEFAULT_WIDTH = 200;

// Base sizes for container elements that scale with width
const BASE_CONTAINER_SIZES = {
  paddingVertical: 12,
  paddingHorizontal: 16,
  headerGap: 8,
  headerPaddingBottom: 8,
  headerMarginBottom: 12,
  titleFontSize: 12,
  titleLetterSpacing: 2,
  dragIconSize: 14,
  loadingFontSize: 10,
  loadingPadding: 8,
};

export function FloorPlanLegend({
  fixtures,
  controls,
  lvProducts,
  config,
  scale,
  isPublicView = false,
  isSimRunning = false,
  onConfigChange,
}: FloorPlanLegendProps) {
  // === ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP ===

  const { categories, isLoading, isEmpty } = useLegendData(fixtures, controls, lvProducts);

  // Get position and size from config or use defaults
  const posX = config?.x ?? DEFAULT_POSITION.x;
  const posY = config?.y ?? DEFAULT_POSITION.y;
  const configWidth = config?.width ?? DEFAULT_WIDTH;

  // Local state for dragging position
  const [isDragging, setIsDragging] = useState(false);
  const [localPos, setLocalPos] = useState({ x: posX, y: posY });

  // Local state for resizing
  const [isResizing, setIsResizing] = useState(false);
  const [localWidth, setLocalWidth] = useState(configWidth);

  // Use refs to track current values for event handlers (avoids stale closures)
  const localPosRef = useRef(localPos);
  const localWidthRef = useRef(localWidth);

  // Track if we just finished dragging to prevent snap-back from stale props
  const justFinishedDraggingRef = useRef(false);
  const justFinishedResizingRef = useRef(false);

  // Drag tracking ref
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);

  // Resize tracking ref
  const resizeStartRef = useRef<{ x: number; startWidth: number } | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    localPosRef.current = localPos;
  }, [localPos]);

  useEffect(() => {
    localWidthRef.current = localWidth;
  }, [localWidth]);

  // Sync local state with props when they change externally
  useEffect(() => {
    if (!isDragging && !justFinishedDraggingRef.current) {
      setLocalPos({ x: posX, y: posY });
    }
    if (justFinishedDraggingRef.current) {
      justFinishedDraggingRef.current = false;
    }
  }, [posX, posY, isDragging]);

  // Sync width from config
  useEffect(() => {
    if (!isResizing && !justFinishedResizingRef.current) {
      setLocalWidth(configWidth);
    }
  }, [configWidth, isResizing]);

  // Reset the justFinishedResizing flag after a delay
  // This prevents async prop updates from snapping width back during resize
  useEffect(() => {
    if (justFinishedResizingRef.current && !isResizing) {
      const timer = setTimeout(() => {
        justFinishedResizingRef.current = false;
      }, 150); // Delay to let async DB updates settle
      return () => clearTimeout(timer);
    }
  }, [isResizing]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (isPublicView) return;

      e.stopPropagation();
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      setIsDragging(true);
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        posX: localPosRef.current.x,
        posY: localPosRef.current.y,
      };
    },
    [isPublicView],
  );

  // Handle drag move and end with document-level listeners
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      // Convert screen delta to canvas coordinates using scale
      const deltaX = (clientX - dragStartRef.current.x) / scale;
      const deltaY = (clientY - dragStartRef.current.y) / scale;

      const newX = dragStartRef.current.posX + deltaX;
      const newY = dragStartRef.current.posY + deltaY;

      setLocalPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      if (dragStartRef.current) {
        // Save the final position using ref (not stale closure)
        onConfigChange({ x: localPosRef.current.x, y: localPosRef.current.y });
        // Mark that we just finished dragging to prevent snap-back from stale props
        justFinishedDraggingRef.current = true;
      }
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, scale, onConfigChange]);

  // Prevent wheel events from propagating (so zooming doesn't affect legend interaction)
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (isPublicView) return;

      e.stopPropagation();
      e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

      setIsResizing(true);
      resizeStartRef.current = {
        x: clientX,
        startWidth: localWidthRef.current,
      };
    },
    [isPublicView],
  );

  // Handle resize move and end with document-level listeners
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!resizeStartRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

      // Use screen delta directly for resize (legend width is in screen pixels)
      // Don't divide by scale - this caused early MAX_WIDTH hits at different zoom levels
      const deltaX = clientX - resizeStartRef.current.x;
      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(MAX_WIDTH, resizeStartRef.current.startWidth + deltaX),
      );

      setLocalWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (resizeStartRef.current) {
        // Save the final width
        onConfigChange({ width: localWidthRef.current });
        justFinishedResizingRef.current = true;
      }
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isResizing, scale, onConfigChange]);

  // === CONDITIONAL RETURN AFTER ALL HOOKS ===

  // Determine if legend should be visible
  // Default to visible when there are symbols, hidden if isEmpty
  const isVisible = config?.visible ?? !isEmpty;

  // Don't render if explicitly hidden or if there are no symbols
  if (!isVisible || isEmpty) {
    return null;
  }

  // Calculate scale factor based on width (icons/fonts scale with legend width)
  const scaleFactor = localWidth / DEFAULT_WIDTH;

  // Calculate scaled container sizes
  const paddingVertical = BASE_CONTAINER_SIZES.paddingVertical * scaleFactor;
  const paddingHorizontal = BASE_CONTAINER_SIZES.paddingHorizontal * scaleFactor;
  const headerGap = BASE_CONTAINER_SIZES.headerGap * scaleFactor;
  const headerPaddingBottom = BASE_CONTAINER_SIZES.headerPaddingBottom * scaleFactor;
  const headerMarginBottom = BASE_CONTAINER_SIZES.headerMarginBottom * scaleFactor;
  const titleFontSize = BASE_CONTAINER_SIZES.titleFontSize * scaleFactor;
  const titleLetterSpacing = BASE_CONTAINER_SIZES.titleLetterSpacing * scaleFactor;
  const dragIconSize = BASE_CONTAINER_SIZES.dragIconSize * scaleFactor;
  const loadingFontSize = BASE_CONTAINER_SIZES.loadingFontSize * scaleFactor;
  const loadingPadding = BASE_CONTAINER_SIZES.loadingPadding * scaleFactor;

  return (
    <div
      data-floor-plan-legend="true"
      style={{
        ...styles.container,
        left: localPos.x,
        top: localPos.y,
        width: localWidth,
        minWidth: 'unset',
        maxWidth: 'unset',
        padding: `${paddingVertical}px ${paddingHorizontal}px`,
        boxShadow:
          isDragging || isResizing ? '0 4px 12px rgba(0,0,0,0.25)' : styles.container.boxShadow,
        zIndex: 50,
      }}
      onWheel={handleWheel}
    >
      {/* Header with drag handle */}
      <div
        style={{
          ...styles.header,
          gap: `${headerGap}px`,
          paddingBottom: `${headerPaddingBottom}px`,
          marginBottom: `${headerMarginBottom}px`,
          ...(isDragging ? styles.headerDragging : {}),
        }}
        onMouseDown={!isPublicView ? handleDragStart : undefined}
        onTouchStart={!isPublicView ? handleDragStart : undefined}
      >
        {!isPublicView && <GripVertical size={dragIconSize} style={styles.dragIcon} />}
        <span
          style={{
            ...styles.title,
            fontSize: `${titleFontSize}px`,
            letterSpacing: `${titleLetterSpacing}px`,
          }}
        >
          Legend
        </span>
        {!isPublicView && <GripVertical size={dragIconSize} style={styles.dragIcon} />}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {isLoading ? (
          <div
            style={{
              ...styles.loading,
              fontSize: `${loadingFontSize}px`,
              padding: `${loadingPadding}px`,
            }}
          >
            Loading symbols...
          </div>
        ) : categories.length === 0 ? (
          <div
            style={{
              ...styles.empty,
              fontSize: `${loadingFontSize}px`,
              padding: `${loadingPadding}px`,
            }}
          >
            No symbols placed
          </div>
        ) : (
          categories.map((category, index) => (
            <LegendCategory
              key={category.domain}
              category={category}
              isFirst={index === 0}
              scale={scaleFactor}
            />
          ))
        )}
      </div>

      {/* Resize handle (bottom-right corner) */}
      {!isPublicView && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: 12,
            height: 12,
            cursor: 'ew-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" style={{ color: '#9CA3AF' }}>
            <path d="M0 8L8 0M3 8L8 3M6 8L8 6" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </div>
      )}
    </div>
  );
}

export default FloorPlanLegend;
